import { H3 } from '@/components'
import { useConfirm, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import styled from 'styled-components'
import type {
  DeleteDriverCardMutationVariables,
  DriverCardQuery,
  DriverCardQueryVariables,
} from '..'
import { DELETE_DRIVER_CARD_MUTATION } from '../mutations'
import { DRIVER_CARD_QUERY } from '../queries'
import { InputArea, OldInput, OldProfileCard } from '../UserProfile/components'

const DeleteButton = styled(Icon)`
  position: absolute;
  top: 0;
  right: 0;
  padding: 1rem;
  &:hover {
    cursor: pointer;
  }
`

const Wrapper = styled(OldProfileCard)`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'header header header'
    'name driverId driverId';
  gap: 1.5rem;

  header {
    grid-area: header;
  }

  ${props => props.theme.media.largePad} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'name'
      'driverId';
    gap: 1rem;
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'name'
      'driverId'
      'expiryDate';
    gap: 1rem;
  }
`

export interface DriverCardProps {
  cardId: string
  active: boolean
  area: string
}

export const DriverCard: React.FC<DriverCardProps> = ({
  cardId,
  active,
  area,
}) => {
  const { data, loading } = useQuery<DriverCardQuery, DriverCardQueryVariables>(
    DRIVER_CARD_QUERY,
    { variables: { id: cardId } }
  )
  const translations = useTranslate({
    labels: {
      name: 'common.name',
      cardNumber: 'users.driver-card',
      expires: 'common.expires-at',
      title: 'users.active-driver-card',
      titleExpired: 'users.expired-driver-card',
      submit: 'common.submit',
      addExpiry: 'users.add-expiry-date',
    },
    confirm: {
      title: 'users.delete-driver-card',
      message: 'users.delete-driver-card-message',
    },
  })
  const confirm = useConfirm()

  const { hasPermissions } = usePermissions()

  const canDelete = hasPermissions(
    [PERMISSIONS.users.change.user, PERMISSIONS.users.delete.user],
    true
  )

  const [deleteDriverCard] = useMutation<{}, DeleteDriverCardMutationVariables>(
    DELETE_DRIVER_CARD_MUTATION,
    {
      refetchQueries: ['Profile'],
    }
  )

  const card = data?.driverCard

  async function handleDelete() {
    const { data } = await confirm(
      translations.confirm.message,
      translations.confirm.title
    )
    if (!data) return
    void deleteDriverCard({ variables: { id: cardId } })
  }

  // TODO: Add loading view on final design
  return loading ? null : (
    <Wrapper $gridArea={area}>
      <header>
        <H3>
          {active
            ? translations.labels.title
            : translations.labels.titleExpired}
        </H3>
      </header>

      <InputArea $area="name">
        <label>{translations.labels.name}</label>
        <OldInput
          editing={false}
          value={card?.user.fullName ?? ''}
          onChange={() => void 0}
        />
      </InputArea>

      <InputArea $area="driverId">
        <label>{translations.labels.cardNumber}</label>
        <OldInput
          editing={false}
          value={`${card?.cardIssuingMemberState}${card?.driverIdentification}`}
          onChange={() => void 0}
        />
      </InputArea>

      {canDelete && (
        <DeleteButton
          icon="times"
          hoverColor="red"
          onClick={() => handleDelete()}
        />
      )}
    </Wrapper>
  )
}
